import React, { useState, useEffect, useContext } from 'react';
import { ListGroup, Button, Form, Spinner, Alert, InputGroup, FormControl, Badge, Row, Col, ButtonGroup } from 'react-bootstrap';
import { AuthContext } from '../../context/AuthContext';
import projectUrlService from '../../services/projectUrlService';

const ProjectUrls = ({ projectId }) => {
  const [urls, setUrls] = useState([]);
  const [urlType, setUrlType] = useState('live');
  const [url, setUrl] = useState('');
  const [editingUrl, setEditingUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Get current user from AuthContext for role-based permissions
  const { currentUser } = useContext(AuthContext);
  
  // Check user role for permissions
  const isAdmin = currentUser && currentUser.role === 'admin';
  const isManager = currentUser && currentUser.role === 'manager';

  useEffect(() => {
    fetchUrls();
  }, [projectId]);

  const fetchUrls = async () => {
    setLoading(true);
    try {
      const projectUrls = await projectUrlService.getProjectUrls(projectId);
      setUrls(projectUrls);
    } catch (err) {
      setError('Failed to load URLs.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrUpdate = async () => {
    if (!urlType || !url) return;
    setLoading(true);
    setError('');
    try {
      if (editingUrl) {
        await projectUrlService.updateProjectUrl(editingUrl.id, { url_type: urlType, url });
      } else {
        await projectUrlService.addProjectUrl(projectId, { url_type: urlType, url });
      }
      resetForm();
      fetchUrls();
    } catch (err) {
      setError(editingUrl ? 'Failed to update URL.' : 'Failed to add URL.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (urlId, isBlurred = false) => {
    let confirmMessage = 'Are you sure you want to delete this URL?';
    
    if (isManager && !isBlurred) {
      confirmMessage = 'This will mark the URL for deletion. An admin will need to complete the deletion. Continue?';
    } else if (isAdmin && isBlurred) {
      confirmMessage = 'This URL was marked for deletion by a manager. Confirm permanent deletion?';
    }
    
    if (!window.confirm(confirmMessage)) return;
    
    setLoading(true);
    setError('');
    try {
      await projectUrlService.deleteProjectUrl(urlId);
      fetchUrls();
    } catch (err) {
      setError('Failed to delete URL.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle unblurring (restoring) a URL that was marked for deletion
  const handleUnblur = async (urlId) => {
    if (!window.confirm('Are you sure you want to restore this URL?')) return;
    setLoading(true);
    setError('');
    try {
      await projectUrlService.unblurProjectUrl(urlId);
      fetchUrls();
    } catch (err) {
      setError('Failed to restore URL.');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (url) => {
    setEditingUrl(url);
    setUrlType(url.url_type);
    setUrl(url.url);
  };

  const resetForm = () => {
    setEditingUrl(null);
    setUrlType('live');
    setUrl('');
  };

  const getUrlTypeIcon = (urlType) => {
    switch (urlType.toLowerCase()) {
      case 'live': return 'bi bi-globe';
      case 'crm': return 'bi bi-people';
      case 'website': return 'bi bi-browser-chrome';
      case 'git': return 'bi bi-git';
      case 'swagger': return 'bi bi-file-earmark-code';
      case 'postman': return 'bi bi-send-check';
      case 'api': return 'bi bi-hdd-network';
      case 'backend': return 'bi bi-server';
      case 'frontend': return 'bi bi-window-desktop';
      case 'docs': return 'bi bi-file-earmark-text';
      case 'figma': return 'bi bi-bezier2';
      case 'trello': return 'bi bi-kanban';
      case 'jira': return 'bi bi-kanban-fill';
      case 'staging': return 'bi bi-ladder';
      case 'test': return 'bi bi-bug';
      case 'database': return 'bi bi-database';
      case 'admin': return 'bi bi-person-lock';
      default: return 'bi bi-link-45deg';
    }
  };

  const getUrlTypeBadgeColor = (urlType) => {
    switch (urlType.toLowerCase()) {
      case 'live': return 'success';
      case 'crm': return 'primary';
      case 'website': return 'info';
      case 'git': return 'dark';
      case 'swagger': return 'success';
      case 'postman': return 'warning';
      case 'api': return 'primary';
      case 'backend': return 'secondary';
      case 'frontend': return 'info';
      case 'docs': return 'light';
      case 'figma': return 'secondary';
      case 'trello': return 'primary';
      case 'jira': return 'info';
      case 'staging': return 'warning';
      case 'test': return 'danger';
      case 'database': return 'dark';
      case 'admin': return 'danger';
      default: return 'secondary';
    }
  };

  // Helper function to render the URL section separately to avoid the "0" issue
  const renderUrlSection = (urlObj) => {
    return (
      <div className="d-flex align-items-center text-truncate" style={{ maxWidth: '70%', opacity: urlObj.is_blurred ? '0.6' : '1' }}>
        <i className={`${getUrlTypeIcon(urlObj.url_type)} me-2`}></i>
        <Badge bg={getUrlTypeBadgeColor(urlObj.url_type)} className="me-2">{urlObj.url_type}</Badge>
        <a href={urlObj.url} target="_blank" rel="noopener noreferrer" className="text-truncate" title={urlObj.url}>
          {urlObj.url}
        </a>
        {urlObj.is_blurred ? 
          <Badge bg="secondary" className="ms-2">Pending Deletion</Badge>
        : null}
      </div>
    );
  };

  return (
    <div className="mb-3">
      <h6 className="mb-2"><i className="bi bi-link me-2"></i>URLs</h6>
      {error && <Alert variant="danger" className="py-1 mb-2">{error}</Alert>}
      
      {/* URL Add/Edit Form */}
      <Row className="mb-2">
        <Col>
          <InputGroup size="sm">
            <Form.Select value={urlType} onChange={(e) => setUrlType(e.target.value)} style={{ maxWidth: '110px' }}>
              <option value="live">Live</option>
              <option value="staging">Staging</option>
              <option value="crm">CRM</option>
              <option value="website">Website</option>
              <option value="admin">Admin</option>
              <option value="api">API</option>
              <option value="swagger">Swagger</option>
              <option value="backend">Backend</option>
              <option value="frontend">Frontend</option>
              <option value="git">Git</option>
              <option value="postman">Postman</option>
              <option value="docs">Docs</option>
              <option value="database">DB</option>
              <option value="test">Test</option>
              <option value="other">Other</option>
            </Form.Select>
            <FormControl size="sm" placeholder="Enter URL" value={url} onChange={(e) => setUrl(e.target.value)} />
            <Button size="sm" onClick={handleAddOrUpdate} disabled={loading}>
              {loading ? <Spinner as="span" animation="border" size="sm" /> : (editingUrl ? <i className="bi bi-check"></i> : <i className="bi bi-plus"></i>)}
            </Button>
            {editingUrl && <Button variant="outline-secondary" size="sm" onClick={resetForm}><i className="bi bi-x"></i></Button>}
          </InputGroup>
        </Col>
      </Row>
      
      {/* URL List */}
      {loading && !urls.length ? (
        <div className="text-center py-2"><Spinner size="sm" animation="border" /></div>
      ) : urls.length > 0 ? (
        <ListGroup variant="flush" className="border rounded">
          {urls.map((urlItem) => (
            <ListGroup.Item 
              key={urlItem.id} 
              className={`py-2 d-flex justify-content-between align-items-center ${urlItem.is_blurred ? 'url-blurred' : ''}`}
            >
              {renderUrlSection(urlItem)}
              <div>
                <ButtonGroup size="sm">
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="px-2 py-0" 
                    onClick={() => startEdit(urlItem)}
                    disabled={urlItem.is_blurred}
                  >
                    <i className="bi bi-pencil"></i>
                  </Button>
                  {(isAdmin || isManager) ? (
                    <Button 
                      variant={urlItem.is_blurred ? "outline-secondary" : "outline-danger"}
                      size="sm" 
                      className="px-2 py-0"
                      onClick={() => handleDelete(urlItem.id, urlItem.is_blurred)}
                      title={urlItem.is_blurred ? "Permanently delete URL" : "Delete URL"}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  ) : null}
                  
                  {(isAdmin && urlItem.is_blurred) ? (
                    <Button 
                      variant="outline-success" 
                      size="sm" 
                      className="px-2 py-0"
                      onClick={() => handleUnblur(urlItem.id)}
                      title="Restore URL"
                    >
                      <i className="bi bi-arrow-counterclockwise"></i>
                    </Button>
                  ) : null}
                </ButtonGroup>
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      ) : (
        <div className="text-center text-muted small py-2">No URLs added</div>
      )}
    </div>
  );
};

export default ProjectUrls;
